import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, FlatList, StyleSheet, Animated, TouchableOpacity, Modal, Alert, RefreshControl, ScrollView, KeyboardAvoidingView, Platform, Keyboard } from 'react-native';
import { useNavigation, useFocusEffect, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import AppShell from '../components/layout/AppShell';
import ScreenCommandHeader from '../components/layout/ScreenCommandHeader';
import TopBar from '../components/layout/TopBar';
import NetworkBusinessCard from '../components/network/NetworkBusinessCard';
import EmptyState from '../components/work/EmptyState';
import ListItemSkeleton from '../components/common/ListItemSkeleton';
import ConnectionTypeModal from '../components/network/ConnectionTypeModal';
import { CustomPicker } from '../components/common/CustomPicker';
import { RootStackParamList } from '../types/navigation';
import { NetworkView, ConnectionType, ConnectionFilter } from '../types/network';
import { useTheme } from '../theme/ThemeContext';
import ThemedText from '../components/common/ThemedText';
import { storage } from '../utils/storage';
import * as networkApi from '../lib/api/network';
import * as adminApi from '../lib/api/admin';
import { showSuccessToast, showErrorToast } from '../utils/toast.native';
import { getCountries, getCitiesForCountry } from '../data/countriesAndCities';
import { INDUSTRIES } from '../data/industries';
import { useAuth } from '../context/AuthContext';

const PROFESSIONS = [
  'Software Engineer',
  'Product Designer',
  'Product Manager',
  'Marketing Manager',
  'Sales Manager',
  'Business Analyst',
  'Data Scientist',
  'DevOps Engineer',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'UX Designer',
  'UI Designer',
  'Content Writer',
  'Copywriting',
  'Project Manager',
  'HR Manager',
  'Finance Manager',
  'Operations Manager',
  'Consultant',
  'Entrepreneur',
  'Freelancer',
  'Other',
];

const getAllCities = (): string[] => {
  const countries = getCountries();
  const allCities = new Set<string>();
  countries.forEach(country => {
    const cities = getCitiesForCountry(country);
    cities.forEach(city => allCities.add(city));
  });
  return Array.from(allCities).sort();
};

type NetworkNavigationProp = StackNavigationProp<RootStackParamList>;
type NetworkRouteProp = RouteProp<RootStackParamList, 'MainTabs'>;

const NetworkScreen: React.FC = () => {
  const navigation = useNavigation<NetworkNavigationProp>();
  const route = useRoute<NetworkRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  
  const isAdmin = user?.role === 'admin';
  
  const initialView = (route.params as any)?.initialView as NetworkView | undefined;
  
  const [selectedView, setSelectedView] = useState<NetworkView>(initialView || 'my-network');
  const [searchQuery, setSearchQuery] = useState('');
  const [myNetworkSearchQuery, setMyNetworkSearchQuery] = useState('');
  const [connectionFilter, setConnectionFilter] = useState<ConnectionFilter>('all');
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  const [searchFilters, setSearchFilters] = useState<{
    city?: string;
    designation?: string;
    industry?: string;
  }>({});
  const [tempSearchFilters, setTempSearchFilters] = useState<{
    city?: string;
    designation?: string;
    industry?: string;
  }>({});
  const [showSearchFilterModal, setShowSearchFilterModal] = useState(false);
  
  const [showConnectionTypeModal, setShowConnectionTypeModal] = useState(false);
  const [pendingConnectionAction, setPendingConnectionAction] = useState<{
    type: 'send' | 'accept';
    id: string;
    userId: string;
    userName: string;
  } | null>(null);
  
  const [connections, setConnections] = useState<networkApi.Connection[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<networkApi.ConnectionRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<networkApi.ConnectionRequest[]>([]);
  const [searchResults, setSearchResults] = useState<networkApi.SearchUser[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  
  const headerTranslate = useMemo(() => {
    if (headerHeight === 0) return 0;
    return scrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, headerHeight]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 150, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: -20, duration: 150, useNativeDriver: true }),
    ]).start(() => {
      slideAnim.setValue(20);
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      ]).start();
    });
  }, [selectedView]);

  const loadConnections = async () => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      const filter = connectionFilter === 'all' ? undefined : connectionFilter;
      const result = await networkApi.getMyConnections(token, filter, myNetworkSearchQuery || undefined);
      setConnections(result.data);
    } catch (error: any) {
      console.error('Error loading connections:', error);
      Alert.alert('Error', error.message || 'Failed to load connections');
    }
  };

  const loadRequests = async () => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      const [incoming, outgoing] = await Promise.all([
        networkApi.getConnectionRequests(token, 'incoming'),
        networkApi.getConnectionRequests(token, 'outgoing'),
      ]);
      setIncomingRequests(incoming.data);
      setOutgoingRequests(outgoing.data);
    } catch (error: any) {
      console.error('Error loading requests:', error);
      Alert.alert('Error', error.message || 'Failed to load requests');
    }
  };

  const loadSearchResults = async () => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      setLoading(true);
      // Load first 10 users by default, or search results if query/filters exist
      const hasFilters = searchQuery.trim() || searchFilters.city || searchFilters.designation || searchFilters.industry;
      const query = searchQuery.trim() || '';
      const result = await networkApi.searchUsers(token, query, 1, hasFilters ? 20 : 10, searchFilters);
      setSearchResults(result.data);
    } catch (error: any) {
      console.error('Error searching users:', error);
      Alert.alert('Error', error.message || 'Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialView) setSelectedView(initialView);
  }, []);

  useEffect(() => {
    if (selectedView === 'my-network') {
      loadConnections();
    } else if (selectedView === 'requests') {
      loadRequests();
    } else if (selectedView === 'search') {
      loadSearchResults();
    }
  }, [selectedView, connectionFilter, myNetworkSearchQuery]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (selectedView === 'search') loadSearchResults();
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [searchQuery, searchFilters, selectedView]);

  useFocusEffect(
    useCallback(() => {
      if (selectedView === 'my-network') {
        loadConnections();
      } else if (selectedView === 'requests') {
        loadRequests();
      }
    }, [selectedView, connectionFilter, myNetworkSearchQuery])
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    if (selectedView === 'my-network') {
      await loadConnections();
    } else if (selectedView === 'requests') {
      await loadRequests();
    } else if (selectedView === 'search') {
      await loadSearchResults();
    }
    setRefreshing(false);
  };

  const handleMessage = (userId: string, userName: string) => {
    navigation.navigate('PersonalChat', { userId, userName });
  };

  const handleViewProfile = (userId: string) => {
    navigation.navigate('Profile', { userId });
  };

  const handleRemoveConnection = async (connectionId: string) => {
    const token = await storage.getToken();
    if (!token) return;

    try {
      await networkApi.removeConnection(token, connectionId);
      await loadConnections();
      showSuccessToast('Connection removed successfully');
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to remove connection');
    }
  };

  const handleAcceptRequest = (request: networkApi.ConnectionRequest) => {
    setPendingConnectionAction({
      type: 'accept',
      id: request.id,
      userId: request.user.id,
      userName: request.user.name || 'User',
    });
    setShowConnectionTypeModal(true);
  };

  const handleRejectRequest = async (requestId: string) => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert('Reject Request', 'Are you sure you want to reject this connection request?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Reject',
        style: 'destructive',
        onPress: async () => {
          try {
            await networkApi.rejectConnectionRequest(token, requestId);
            await loadRequests();
          } catch (error: any) {
            Alert.alert('Error', error.message || 'Failed to reject request');
          }
        },
      },
    ]);
  };

  const handleSendRequest = (user: networkApi.SearchUser) => {
    setPendingConnectionAction({
      type: 'send',
      id: user.id,
      userId: user.id,
      userName: user.name || 'User',
    });
    setShowConnectionTypeModal(true);
  };

  const handleConnectionTypeSelect = async (connectionType: ConnectionType) => {
    if (!pendingConnectionAction) return;

    const token = await storage.getToken();
    if (!token) return;

    try {
      if (pendingConnectionAction.type === 'send') {
        await networkApi.sendConnectionRequest(token, pendingConnectionAction.userId, connectionType);
        showSuccessToast('Connection request sent!');
        await loadSearchResults();
      } else if (pendingConnectionAction.type === 'accept') {
        await networkApi.acceptConnectionRequest(token, pendingConnectionAction.id, connectionType);
        showSuccessToast('Connection request accepted!');
        await loadRequests();
        await loadConnections();
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to process request');
    } finally {
      setShowConnectionTypeModal(false);
      setPendingConnectionAction(null);
    }
  };

  const handleConnectionTypeCancel = () => {
    setShowConnectionTypeModal(false);
    setPendingConnectionAction(null);
  };

  const handleToggleRole = async (userId: string, currentRole: string) => {
    const token = await storage.getToken();
    if (!token) return;

    const action = currentRole === 'admin' ? 'remove admin role from' : 'make admin';
    
    Alert.alert(
      'Confirm Action',
      `Are you sure you want to ${action} this user?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: currentRole === 'admin' ? 'destructive' : 'default',
          onPress: async () => {
            try {
              const result = await adminApi.toggleUserRole(token, userId);
              showSuccessToast(result.message);
              await loadSearchResults();
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to update user role');
            }
          },
        },
      ]
    );
  };

  const handleDeactivateUser = async (userId: string) => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert(
      'Deactivate User',
      'Are you sure you want to deactivate this user? They will not be able to use the platform.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Deactivate',
          style: 'destructive',
          onPress: async () => {
            try {
              const result = await adminApi.deactivateUser(token, userId);
              showSuccessToast(result.message);
              await loadSearchResults();
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to deactivate user');
            }
          },
        },
      ]
    );
  };

  const handleReactivateUser = async (userId: string) => {
    const token = await storage.getToken();
    if (!token) return;

    Alert.alert(
      'Reactivate User',
      'Are you sure you want to reactivate this user? They will regain access to the platform.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reactivate',
          onPress: async () => {
            try {
              const result = await adminApi.reactivateUser(token, userId);
              showSuccessToast(result.message);
              await loadSearchResults();
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to reactivate user');
            }
          },
        },
      ]
    );
  };

  const renderMyNetwork = () => {
    if (loading && connections.length === 0) {
      return (
        <View>
          <ListItemSkeleton />
          <ListItemSkeleton />
          <ListItemSkeleton />
        </View>
      );
    }

    if (connections.length === 0) {
      return (
        <EmptyState
          icon="people-outline"
          message={myNetworkSearchQuery.trim() ? "No connections found" : "You don't have any connections yet"}
        />
      );
    }

    return (
      <Animated.FlatList
        data={connections}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <NetworkBusinessCard
            user={{
              id: item.user.id,
              name: item.user.name || 'Unknown',
              profession: item.user.profession || '',
              city: item.user.city || '',
              photo: item.user.photo ?? undefined,
              businessName: item.user.businessName ?? undefined,
              businessIndustry: item.user.businessIndustry ?? undefined,
              businessPhoto: item.user.businessPhoto ?? undefined,
              industry: item.user.industry ?? undefined,
              designation: item.user.designation ?? undefined,
            }}
            connectionType={item.connectionType}
            variant="connection"
            onMessage={(userId) => handleMessage(userId, item.user.name || 'Unknown')}
            onViewProfile={handleViewProfile}
            onRemove={() => handleRemoveConnection(item.id)}
          />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: true })}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={theme.accent} />}
      />
    );
  };

  const renderRequests = () => {
    if (loading && incomingRequests.length === 0 && outgoingRequests.length === 0) {
      return (
        <View>
          <ListItemSkeleton />
          <ListItemSkeleton />
          <ListItemSkeleton />
        </View>
      );
    }

    if (incomingRequests.length === 0 && outgoingRequests.length === 0) {
      return <EmptyState icon="mail-outline" message="No connection requests" />;
    }

    type SectionItem = { type: 'section'; title: string };
    type RequestItem = { type: 'incoming' | 'outgoing'; request: networkApi.ConnectionRequest };
    type ListItem = SectionItem | RequestItem;

    const listData: ListItem[] = [
      { type: 'section', title: 'Incoming Requests' },
      ...incomingRequests.map(request => ({ type: 'incoming' as const, request })),
      { type: 'section', title: 'Outgoing Requests' },
      ...outgoingRequests.map(request => ({ type: 'outgoing' as const, request })),
    ];

    return (
      <Animated.FlatList
        data={listData}
        keyExtractor={(item, index) => {
          if (item.type === 'section') return `section-${index}`;
          return item.request.id;
        }}
        renderItem={({ item }) => {
          if (item.type === 'section') {
            return (
              <View style={[styles.sectionHeader, { backgroundColor: theme.background }]}>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>{item.title}</ThemedText>
              </View>
            );
          }
          return (
            <NetworkBusinessCard
              user={{
                id: item.request.user.id,
                name: item.request.user.name || 'Unknown',
                profession: item.request.user.profession || '',
                city: item.request.user.city || '',
                photo: item.request.user.photo ?? undefined,
                businessName: item.request.user.businessName ?? undefined,
                businessIndustry: item.request.user.businessIndustry ?? undefined,
                businessPhoto: item.request.user.businessPhoto ?? undefined,
                industry: item.request.user.industry ?? undefined,
                designation: item.request.user.designation ?? undefined,
              }}
              variant={item.type === 'incoming' ? 'incoming-request' : 'outgoing-request'}
              onAccept={item.type === 'incoming' ? () => handleAcceptRequest(item.request) : undefined}
              onReject={item.type === 'incoming' ? () => handleRejectRequest(item.request.id) : undefined}
              onViewProfile={handleViewProfile}
            />
          );
        }}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: false })}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={theme.accent} />}
      />
    );
  };

  const renderSearch = () => {
    if (loading) {
      return (
        <View>
          <ListItemSkeleton />
          <ListItemSkeleton />
          <ListItemSkeleton />
        </View>
      );
    }

    if (searchResults.length === 0) {
      return <EmptyState icon="search-outline" message={searchQuery.trim() ? "No users found" : "No users available"} />;
    }

    return (
      <Animated.FlatList
        data={searchResults}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <NetworkBusinessCard
            user={{
              id: item.id,
              name: item.name || 'Unknown',
              profession: item.profession || '',
              city: item.city || '',
              photo: item.photo ?? undefined,
              businessName: item.businessName ?? undefined,
              businessIndustry: item.businessIndustry ?? undefined,
              businessPhoto: item.businessPhoto ?? undefined,
              industry: item.industry ?? undefined,
              designation: item.designation ?? undefined,
              role: item.role ?? undefined,
            }}
            variant="search"
            connectionStatus={item.connectionStatus}
            isAdmin={isAdmin}
            onSendRequest={() => handleSendRequest(item)}
            onMessage={(userId) => handleMessage(userId, item.name || 'Unknown')}
            onViewProfile={handleViewProfile}
            onToggleRole={handleToggleRole}
            onDeactivate={handleDeactivateUser}
            onReactivate={handleReactivateUser}
          />
        )}
        contentContainerStyle={[styles.searchListContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: false })}
      />
    );
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'my-network':
        return renderMyNetwork();
      case 'requests':
        return renderRequests();
      case 'search':
        return renderSearch();
      default:
        return renderMyNetwork();
    }
  };

  return (
    <AppShell title="Network" scrollable={false} showTopBar={false}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          <TopBar title="Network" showLogo={false} />
          <ScreenCommandHeader
            segments={[
              { value: 'my-network' as const, label: 'My Network' },
              { value: 'requests' as const, label: 'Requests' },
              { value: 'search' as const, label: 'Search' },
            ]}
            selectedValue={selectedView}
            onValueChange={setSelectedView}
            searchConfig={selectedView === 'my-network' ? {
              value: myNetworkSearchQuery,
              onChange: setMyNetworkSearchQuery,
              placeholder: 'Search connections...',
              onClear: () => setMyNetworkSearchQuery(''),
            } : selectedView === 'search' ? {
              value: searchQuery,
              onChange: setSearchQuery,
              placeholder: 'Search by name or business',
              onClear: () => {
                setSearchQuery('');
                setSearchFilters({});
              },
            } : undefined}
            actionComponent={selectedView === 'my-network' ? (
              <TouchableOpacity
                style={[styles.filterButton, { backgroundColor: theme.backgroundTertiary }]}
                onPress={() => setShowFilterModal(true)}
                activeOpacity={0.7}
              >
                <Ionicons name="funnel-outline" size={20} color={theme.text} />
                {connectionFilter !== 'all' && (
                  <View style={[styles.filterBadge, { backgroundColor: theme.accent }]} />
                )}
              </TouchableOpacity>
            ) : selectedView === 'search' ? (
              <TouchableOpacity
                style={[styles.filterButton, { backgroundColor: theme.backgroundTertiary }]}
                onPress={() => {
                  setTempSearchFilters(searchFilters);
                  setShowSearchFilterModal(true);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="options-outline" size={20} color={theme.text} />
                {Object.keys(searchFilters).filter(k => searchFilters[k as keyof typeof searchFilters]).length > 0 && (
                  <View style={[styles.filterBadge, { backgroundColor: theme.accent }]} />
                )}
              </TouchableOpacity>
            ) : undefined}
          />
        </Animated.View>

        <Animated.View 
          style={[
            styles.content,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {renderContent()}
        </Animated.View>

        {/* Connection Type Modal */}
        {pendingConnectionAction && (
          <ConnectionTypeModal
            visible={showConnectionTypeModal}
            userName={pendingConnectionAction.userName}
            mode={pendingConnectionAction.type}
            onSelect={handleConnectionTypeSelect}
            onCancel={handleConnectionTypeCancel}
          />
        )}

        {/* Filter Modal for My Network */}
        <Modal
          visible={showFilterModal}
          transparent
          animationType="slide"
          onRequestClose={() => setShowFilterModal(false)}
        >
          <TouchableOpacity 
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setShowFilterModal(false)}
          >
            <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()}>
              <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
                <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
                  <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                    Filter Connections
                  </ThemedText>
                  <TouchableOpacity onPress={() => setShowFilterModal(false)}>
                    <Ionicons name="close" size={24} color={theme.textSecondary} />
                  </TouchableOpacity>
                </View>

                <View style={styles.modalBody}>
                  {[
                    { value: 'all' as const, label: 'All Connections', icon: 'people-outline', color: theme.accent },
                    { value: 'mentor' as const, label: 'Mentors', icon: 'school-outline', color: '#4CAF50' },
                    { value: 'client' as const, label: 'Clients', icon: 'briefcase-outline', color: '#2196F3' },
                    { value: 'supplier' as const, label: 'Suppliers', icon: 'cube-outline', color: '#FF9800' },
                  ].map((filter) => (
                    <TouchableOpacity
                      key={filter.value}
                      style={[
                        styles.filterOption,
                        {
                          backgroundColor: connectionFilter === filter.value ? filter.color + '10' : theme.backgroundSecondary,
                          borderColor: connectionFilter === filter.value ? filter.color : theme.border,
                        },
                      ]}
                      onPress={() => {
                        setConnectionFilter(filter.value);
                        setShowFilterModal(false);
                      }}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.filterIcon, { backgroundColor: filter.color + '20' }]}>
                        <Ionicons name={filter.icon as any} size={24} color={filter.color} />
                      </View>
                      <ThemedText
                        style={[
                          styles.filterOptionText,
                          { color: connectionFilter === filter.value ? filter.color : theme.text },
                        ]}
                      >
                        {filter.label}
                      </ThemedText>
                      {connectionFilter === filter.value && (
                        <Ionicons name="checkmark-circle" size={24} color={filter.color} />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>

        {/* Search Filter Modal */}
        <Modal
          visible={showSearchFilterModal}
          transparent
          animationType="slide"
          onRequestClose={() => {
            Keyboard.dismiss();
            setShowSearchFilterModal(false);
          }}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={styles.modalOverlay}
          >
            <TouchableOpacity 
              style={{ flex: 1 }}
              activeOpacity={1}
              onPress={() => {
                Keyboard.dismiss();
                setShowSearchFilterModal(false);
              }}
            />
            <View style={[styles.modalContent, { backgroundColor: theme.card, maxHeight: '80%' }]}>
              <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
                <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                  Search Filters
                </ThemedText>
                <TouchableOpacity onPress={() => {
                  Keyboard.dismiss();
                  setShowSearchFilterModal(false);
                }}>
                  <Ionicons name="close" size={24} color={theme.textSecondary} />
                </TouchableOpacity>
              </View>

              <ScrollView 
                style={styles.modalBody} 
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.filterInputContainer}>
                  <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                    City
                  </ThemedText>
                  <CustomPicker
                    selectedValue={tempSearchFilters.city}
                    onValueChange={(value) => setTempSearchFilters({ ...tempSearchFilters, city: value })}
                    items={[
                      { label: 'All Cities', value: undefined },
                      ...getAllCities().map(city => ({
                        label: city,
                        value: city,
                      })),
                    ]}
                    placeholder="Select city"
                    searchable
                    searchPlaceholder="Search cities"
                  />
                </View>

                <View style={styles.filterInputContainer}>
                  <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                    Role / Designation
                  </ThemedText>
                  <CustomPicker
                    selectedValue={tempSearchFilters.designation}
                    onValueChange={(value) => setTempSearchFilters({ ...tempSearchFilters, designation: value })}
                    items={[
                      { label: 'All Roles', value: undefined },
                      ...PROFESSIONS.map(profession => ({
                        label: profession,
                        value: profession,
                      })),
                    ]}
                    placeholder="Select role"
                    searchable
                    searchPlaceholder="Search roles"
                  />
                </View>

                <View style={styles.filterInputContainer}>
                  <ThemedText style={[styles.filterLabel, { color: theme.text }]}>
                    Industry
                  </ThemedText>
                  <CustomPicker
                    selectedValue={tempSearchFilters.industry}
                    onValueChange={(value) => setTempSearchFilters({ ...tempSearchFilters, industry: value })}
                    items={[
                      { label: 'All Industries', value: undefined },
                      ...INDUSTRIES.map(industry => ({
                        label: industry,
                        value: industry,
                      })),
                    ]}
                    placeholder="Select industry"
                    searchable
                    searchPlaceholder="Search industries"
                  />
                </View>

                <View style={styles.filterButtonsContainer}>
                  <TouchableOpacity
                    style={[styles.resetButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                    onPress={() => {
                      setTempSearchFilters({});
                    }}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="refresh-outline" size={20} color={theme.text} />
                    <ThemedText style={{ color: theme.text, marginLeft: 8, fontWeight: '600' }}>Reset</ThemedText>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.applyButton, { backgroundColor: theme.accent }]}
                    onPress={() => {
                      setSearchFilters(tempSearchFilters);
                      Keyboard.dismiss();
                      setShowSearchFilterModal(false);
                    }}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="checkmark-outline" size={20} color="#FFFFFF" />
                    <ThemedText style={{ color: '#FFFFFF', marginLeft: 8, fontWeight: '700' }}>Apply Filters</ThemedText>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </View>
          </KeyboardAvoidingView>
        </Modal>
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  content: {
    flex: 1,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 999,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  filterBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  listContent: {
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  searchListContent: {
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 40,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  modalBody: {
    padding: 16,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 12,
  },
  filterIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  filterOptionText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
  },
  filterInputContainer: {
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  filterButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
    paddingBottom: 8,
  },
  resetButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  applyButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
  },
});

export default NetworkScreen;
